import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { forwardRef, memo } from "react";
import { ButtonBase } from "./ButtonBase";
import { Typography } from "./Typography";
import { CloseOutline } from "../icons/icons";
import { useColorable } from "../providers/ColorsProvider";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
export const chipClasses = {
    root: "MinervaChip-root",
    outlined: "MinervaChip-outlined",
    tonal: "MinervaChip-tonal",
    filled: "MinervaChip-filled",
    selected: "MinervaChip-selected",
    disabled: "MinervaChip-disabled",
};
export const chipSlots = {
    label: Typography,
};
export const chipVariants = ["filled", "tonal", "outlined"];
export const Chip = memo(forwardRef((props, ref) => {
    const { variant = "outlined", startNode, endNode, density = "comfortable", color = "surface", colorSet, invertColors, slots, slotProps, selected, disabled, className, children, sx, ...rest } = useDefaultProps({ component: "Chip", props });
    const colorStyles = useColorable({
        color,
        colorSet: colorSet ? colorSet : variant === "tonal" ? "container" : "main",
        invertColors: invertColors ? invertColors : variant !== "filled" && variant !== "tonal",
    });
    return (_jsx(SlotsProvider, { baseSlots: chipSlots, slots: slots, children: ({ label: Label }) => (_jsxs(ButtonBase, { ref: ref, disabled: disabled, className: clsx([
                className,
                chipClasses.root,
                variant === "outlined" && chipClasses.outlined,
                variant === "tonal" && chipClasses.tonal,
                variant === "filled" && chipClasses.filled,
                selected && chipClasses.selected,
                disabled && chipClasses.disabled,
            ]), sx: mergeSx({
                ...colorStyles,
                paddingY: density === "comfortable" ? 2 : density === "dense" ? 1 : 0,
                paddingLeft: density === "comfortable"
                    ? startNode
                        ? 2
                        : 4
                    : density === "dense"
                        ? startNode
                            ? 1
                            : 3
                        : 0,
                paddingRight: density === "comfortable"
                    ? endNode || selected
                        ? 2
                        : 4
                    : density === "dense"
                        ? endNode || selected
                            ? 1
                            : 3
                        : 0,
                display: "flex",
                gap: density === "comfortable" ? 2 : density === "dense" ? 1 : 0,
                alignItems: "center",
                maxWidth: "100%",
                width: "fit-content",
                minWidth: 0,
                borderRadius: (theme) => theme.radius.default,
                [`&.${chipClasses.outlined}`]: {
                    backgroundColor: "transparent",
                    boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.outline}`,
                    [`&.${chipClasses.selected}`]: {
                        boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.outline}`,
                    },
                },
                [`&.${chipClasses.disabled}`]: {
                    ...colorStyles,
                    [`&.${chipClasses.outlined}`]: {
                        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.disabled.main}`,
                    },
                },
            }, sx), ...rest, children: [startNode, typeof children === "string" ? (_jsx(Label, { truncate: true, variant: "body3", fontWeight: "semibold", ...slotProps?.label, children: children }, "chip-label")) : (children), endNode, selected && _jsx(CloseOutline, { size: "xs" })] })) }));
}));
//# sourceMappingURL=Chip.js.map